import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImg from '../img/rps_logo_dark.png';

const round2 = (val) => typeof val === 'number' ? val.toFixed(2) : val;

const ExportPdf = async (selfAssessment, documents, overall, summary) => {
    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
    });

    const margin = 40;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const maxContentHeight = pageHeight - 2 * margin;
    let y = margin;

    // Helper function to check if we need a new page
    const checkPageBreak = (neededHeight) => {
        if (y + neededHeight > pageHeight - margin) {
            pdf.addPage();
            y = margin;
        }
    };

    // Helper function to convert image to base64
    const getBase64FromImage = (imgPath) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                const dataURL = canvas.toDataURL('image/png');
                resolve(dataURL);
            };
            img.onerror = (error) => {
                console.warn('Could not load logo image:', error);
                resolve(null); // Resolve with null if image fails to load
            };
            img.src = imgPath;
        });
    };

    // Load and add logo
    const logoWidth = 150;
    const logoHeight = 82.81;
    
    try {
        const logoBase64 = await getBase64FromImage(logoImg);
        if (logoBase64) {
            pdf.addImage(logoBase64, 'PNG', margin, y, logoWidth, logoHeight);
        }
    } catch (error) {
        console.warn('Failed to load logo:', error);
    }

    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text('Evaluation Results', margin + logoWidth + 10, y + 41);
    y += 110;

    // === Self-Assessment ===
    checkPageBreak(40);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Self-Assessment Scores', margin, y);
    y += 20;

    const selfScores = selfAssessment?.scores || {};
    const selfRows = Object.entries(selfScores).map(([criteria, data]) => [
        criteria,
        data.score,
        data.max_score,
        `${round2(data.percent)}%`,
    ]);

    if (selfRows.length > 0) {
        autoTable(pdf, {
            startY: y,
            head: [['Criteria', 'Score', 'Max', '%']],
            body: selfRows,
            margin: { left: margin, right: margin },
            styles: { fontSize: 10, font: 'helvetica' },
            headStyles: {
                fillColor: [0, 64, 91],
                textColor: 255,
            },
        });
        y = pdf.lastAutoTable.finalY + 10;
    } else {
        pdf.setFontSize(10);
        pdf.text('No data available.', margin, y);
        y += 20;
    }

    // Overall Self Score
    checkPageBreak(30);
    const overallPercent = selfAssessment?.overall_percent ?? 0;
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Overall Self-Assessment Score: ${round2(overallPercent)}%`, margin, y);
    y += 30;

    // === Documents ===
    checkPageBreak(40);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Document Scores & Comments', margin, y);
    y += 12;

    // Add scoring criteria
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const scoringText = [
        'Scoring:',
        '• Fully present, clear, complete, consistent: 10 points',
        '• Partially present/incomplete: 5 points',
        '• Missing or inconsistent: 0 points'
    ];
    scoringText.forEach(line => {
        pdf.text(line, margin, y);
        y += 12; // spacing between lines
    });

    const docEntries = Object.entries(documents || {});
    if (docEntries.length > 0) {
        const docRows = docEntries.map(([filename, result]) => [
            filename,
            result.score ?? 'N/A',
            Array.isArray(result.comments)
                ? result.comments.join('\n')
                : (result.comments ?? '')
        ]);

        autoTable(pdf, {
            startY: y,
            head: [['Document', 'Score', 'Comments']],
            body: docRows,
            margin: { left: margin, right: margin },
            styles: { fontSize: 10, font: 'helvetica' },
            headStyles: {
                fillColor: [0, 64, 91],
                textColor: 255,
            },
            alternateRowStyles: { fillColor: [240, 240, 240] },
        });
        y = pdf.lastAutoTable.finalY + 10;
    } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text('No document data available.', margin, y);
        y += 20;
    }

    // === Overall Results ===
    y += 12
    checkPageBreak(100);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Overall Results', margin, y);
    y += 20;

    const docAvg = round2((overall?.document_score_avg || 0) * 10);
    const selfAvg = round2(overall?.self_assessment_percent || 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(`Self-Assessment Average: ${selfAvg}%`, margin, y);
    y += 15;
    pdf.text(`Document Score Average: ${docAvg}%`, margin, y);
    y += 25;

    // Strengths & Weaknesses
    const strengths = overall?.strengths || [];
    const weaknesses = overall?.weaknesses || [];
    const maxRows = Math.max(strengths.length, weaknesses.length, 1);
    const swRows = Array.from({ length: maxRows }).map((_, i) => [
        strengths[i] || (i === 0 ? 'None' : ''),
        weaknesses[i] || (i === 0 ? 'None' : ''),
    ]);

    autoTable(pdf, {
        startY: y,
        head: [['Strengths (>=75%)', 'Weaknesses/Areas for Improvement (<50%)']],
        body: swRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 10, font: 'helvetica' },
        headStyles: {
            fillColor: [0, 64, 91],
            textColor: 255,
        },
    });
    y = pdf.lastAutoTable.finalY + 20;

    // === Summary ===
    checkPageBreak(40);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Summary & Recommendations', margin, y);
    y += 20;

    // Handle summary text with proper page breaks
    const summaryText = summary || 'No summary available.';
    
    // Split the summary into sections for better formatting
    const summaryLines = summaryText.split('\n');
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    for (let i = 0; i < summaryLines.length; i++) {
        const line = summaryLines[i];
        
        // Handle different markdown-style headings
        if (line.startsWith('#### ')) {
            checkPageBreak(25);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            const headerText = line.replace(/^#### /, '');
            pdf.text(headerText, margin, y);
            y += 20;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
        } else if (line.startsWith('### ')) {
            checkPageBreak(30);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            const headerText = line.replace(/^### /, '');
            pdf.text(headerText, margin, y);
            y += 25;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
        } else if (line.trim() === '') {
            // Empty line - add some space
            y += 10;
        } else {
            // Regular text line - handle word wrapping and page breaks
            const wrappedLines = pdf.splitTextToSize(line, pageWidth - 2 * margin);
            
            // Check if we need a page break for all the wrapped lines
            const lineHeight = 14;
            const totalHeight = wrappedLines.length * lineHeight;
            checkPageBreak(totalHeight);
            
            // Add each wrapped line
            for (let j = 0; j < wrappedLines.length; j++) {
                if (y + lineHeight > pageHeight - margin) {
                    pdf.addPage();
                    y = margin;
                }
                pdf.text(wrappedLines[j], margin, y);
                y += lineHeight;
            }
        }
    }

    // === Footer ===
    const dateStr = new Date().toLocaleDateString();
    
    // Go to the last page to add footer
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 60, pageHeight - 20);
        pdf.text(`Generated on ${dateStr}`, margin, pageHeight - 20);
    }

    // Save
    pdf.save('evaluation_results.pdf');
};

export default ExportPdf;